import { createClient } from "contentful";

import {
  CONTENT_TYPE_BLOG,
  CONTENT_TYPE_BLOG_TAG,
  CONTENT_TYPE_CAREERS,
  CONTENT_TYPE_LANDING_PAGE,
  CONTENT_TYPE_NAVIGATION,
  CONTENT_TYPE_PAGE,
  CONTENTFUL_PAGE_LIMIT,
} from "@/constants/contentful";

export class ContentfulService {
  private client = createClient({
    space: process.env.CONTENTFUL_SPACE_ID!,
    accessToken: process.env.CONTENTFUL_CONTENT_ACCESS_TOKEN!,
    environment: process.env.CONTENTFUL_ENVIRONMENT!,
  });

  async fetchEntryBySlug(slug: string, content_type: string) {
    return await this.client.getEntries({
      content_type: content_type,
      "fields.slug": slug,
      include: 10,
    });
  }

  async fetchAllPages() {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_PAGE,
      select: ["fields.slug"],
      include: 1,
    });
  }

  async fetchAllBlogPosts() {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_BLOG,
      "fields.platformOnly[ne]": "true",
      select: ["fields.slug"],
      include: 1,
    });
  }

  async fetchAllLandingPages() {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_LANDING_PAGE,
      select: ["fields.slug"],
      include: 1,
    });
  }

  async fetchBlogPostsByTag(tag: string, page: number) {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_BLOG,
      "fields.platformOnly[ne]": "true",
      links_to_entry: tag,
      order: ["-fields.date"],
      skip: page * CONTENTFUL_PAGE_LIMIT,
      limit: CONTENTFUL_PAGE_LIMIT,
      include: 2,
    });
  }

  async fetchLatestBlogPostsByTag(tag?: string) {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_BLOG,
      ...(tag && { links_to_entry: tag }),
      "fields.platformOnly[ne]": "true",
      order: ["-fields.date"],
      limit: 3,
      include: 2,
    });
  }

  async fetchBlogPosts(page: number) {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_BLOG,
      "fields.platformOnly[ne]": "true",
      order: ["-fields.date"],
      skip: page * CONTENTFUL_PAGE_LIMIT,
      limit: CONTENTFUL_PAGE_LIMIT,
      include: 2,
    });
  }

  async fetchCareers() {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_CAREERS,
      order: ["fields.title"],
      include: 1,
    });
  }

  async fetchBlogPostsBySearch(query: string, page: number) {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_BLOG,
      query: query,
      "fields.platformOnly[ne]": "true",
      order: ["-fields.date"],
      skip: page * CONTENTFUL_PAGE_LIMIT,
      limit: CONTENTFUL_PAGE_LIMIT,
      include: 2,
    });
  }

  async fetchBlogTags() {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_BLOG_TAG,
      order: ["fields.title"],
    });
  }

  async fetchFeaturedBlogPosts() {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_BLOG,
      "fields.featured": "true",
      "fields.platformOnly[ne]": "true",
      order: ["-fields.date"],
      limit: 1,
      include: 1,
    });
  }

  async getBlogPosts(page = 0, tag?: string) {
    try {
      const content =
        tag === undefined
          ? await this.fetchBlogPosts(page)
          : await this.fetchBlogPostsByTag(tag, page);
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getLatestBlogPosts(tag?: string) {
    try {
      const content = await this.fetchLatestBlogPostsByTag(tag);
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getCareers() {
    try {
      const entries = await this.fetchCareers();

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async searchBlogPosts(query: string, page: number) {
    try {
      const content = await this.fetchBlogPostsBySearch(query, page);
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getAllPages() {
    try {
      const content: any = await this.fetchAllPages();
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getAllBlogPosts() {
    try {
      const content: any = await this.fetchAllBlogPosts();
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getAllLandingPages() {
    try {
      const content: any = await this.fetchAllLandingPages();
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getFeaturedBlogPosts() {
    try {
      const content: any = await this.fetchFeaturedBlogPosts();
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getBlogTags() {
    try {
      const content: any = await this.fetchBlogTags();
      const entries = content.items;

      return entries;
    } catch (error) {
      console.error(error);
    }
  }

  async getTag(tag: string) {
    try {
      const content: any = await this.fetchEntryBySlug(
        tag,
        CONTENT_TYPE_BLOG_TAG,
      );
      const entry: { sys: any; fields: any } = content.items[0];

      return entry;
    } catch (error) {
      console.error(error);
    }
  }

  async getEntryBySlug(slug: string, content_type: string) {
    try {
      const content: any = await this.fetchEntryBySlug(slug, content_type);
      const entry: { sys: any; fields: any } = content.items[0];

      return entry;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async fetchNavigationByTitle(title: string) {
    return await this.client.getEntries({
      content_type: CONTENT_TYPE_NAVIGATION,
      "fields.title": title,
      include: 10,
    });
  }

  async getHeader() {
    try {
      const content: any =
        await this.fetchNavigationByTitle("Header Navigation");
      const entry: { sys: any; fields: any } = content.items[0];

      return entry;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
